import { CloseIcon, PlusIcon } from "vega-icons";

import { type DealOverviewSectionRequest } from "@vega-api/generated/types/dealOverviewSectionRequest";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { ThemedDropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { type FormMetric } from "@vega-modules/scenes/CoInvestmentsGP/utils/metricTransforms";

import {
  ACS_TEAM_CONTACT_NAME,
  ACS_TEAM_CONTACT_OPTIONS,
  MODAL_TYPES,
  type ModalType,
} from "./constants/modalConstants";
import { MetricValueField } from "./MetricValueField";

type DealOverviewSectionProps = {
  title: string;
  description: string;
  buttonLabel: string;
  items: DealOverviewSectionRequest[] | FormMetric[];
  isAddDisabled: boolean;
  sectionType: ModalType;
  onAdd: () => void;
  onRemove: (name: string) => void;
  getFieldName: (index: number, field: string) => string;
  isRemoving?: boolean;
  editable?: boolean;
  dealCurrency?: CoreCurrency;
};

export const DealOverviewSection = ({
  title,
  description,
  buttonLabel,
  items,
  isAddDisabled,
  sectionType,
  onAdd,
  onRemove,
  getFieldName,
  isRemoving = false,
  editable,
  dealCurrency,
}: DealOverviewSectionProps) => {
  const isMetric = sectionType === MODAL_TYPES.METRIC;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0">
          <h4 className="body-semi-large-medium mb-0 text-text/primary">
            {title}
          </h4>
          <p className="body-base-regular mt-0 text-text/secondary">
            {description}
          </p>
        </div>
        {editable ? (
          <ThemedButton
            className="min-h-7 h-9 px-3"
            dataTestId={`add-${sectionType}-button`}
            disabled={isAddDisabled}
            icon={<PlusIcon className="size-5" />}
            onClick={onAdd}
            type="button"
            variant="secondary"
          >
            {buttonLabel}
          </ThemedButton>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4">
        {items.map((item, index) => {
          const metricType = isMetric && "type" in item ? item.type : undefined;
          const isItemEditable = editable && item.isEditable;
          const isACSContactSelected =
            !isMetric && item.name === ACS_TEAM_CONTACT_NAME;

          return (
            <div className="p-4 border  rounded-md" key={item.id}>
              <div className="flex items-center gap-4">
                <div
                  className={
                    isMetric
                      ? "flex-1 grid grid-cols-2 gap-4"
                      : "flex-1 space-y-4"
                  }
                >
                  <InputField
                    disabled={!isItemEditable}
                    isOptional={false}
                    label={m.coInvestments_lpDealOverviewContent_label()}
                    name={getFieldName(index, "name")}
                    placeholder={m.coInvestments_lpDealOverviewContent_enterCustomLabel()}
                  />
                  {isMetric ? (
                    <MetricValueField
                      dealCurrency={dealCurrency}
                      editable={isItemEditable}
                      fieldName={getFieldName(index, "value")}
                      metricType={metricType}
                    />
                  ) : null}
                  {isACSContactSelected ? (
                    <ThemedDropdownField
                      isClearable={false}
                      label={m.coInvestments_lpDealOverviewContent_value()}
                      name={getFieldName(index, "content")}
                      options={ACS_TEAM_CONTACT_OPTIONS}
                    />
                  ) : null}
                  {!isMetric && !isACSContactSelected ? (
                    <TextareaField
                      disabled={!isItemEditable}
                      isRequired
                      label={m.coInvestments_lpDealOverviewContent_content()}
                      name={getFieldName(index, "content")}
                      placeholder={m.coInvestments_lpDealOverviewContent_enterContent()}
                      rows={4}
                    />
                  ) : null}
                </div>
                {isItemEditable ? (
                  <ThemedIconButton
                    className="rounded-full bg-background/secondary mt-4"
                    dataTestId={`remove-${sectionType}-${index}-button`}
                    disabled={isRemoving}
                    icon={<CloseIcon className="text-text/primary w-4 h-4" />}
                    onClick={() => onRemove(item.name)}
                    size="extraSmall"
                    variant="noBackground"
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
